import { ActionModal } from "@tournated-com/ui";
import { useEffect, useState } from "react";

import {
  useDashboardImpersonationControllerAllowedApps,
  useDashboardImpersonationControllerImpersonate,
} from "@/api/__generated__/dashboard-auth/dashboard-auth";
import { LoginApp } from "@/api/__generated__/schemas/loginApp";
import { env } from "@/env";
import { useT } from "@/i18n";
import { Alert, NumberField, RadioBox, RadioBoxGroup, Skeleton, toast } from "@/ui";
import { getApiErrorCode } from "@/utils";
import { buildImpersonationUrl } from "@/utils/auth/impersonation";
import { buildScopedPath, getTournamentUrl } from "@/utils/common";
import { getUserFullName } from "@/utils/user";

import {
  resolveAvailableImpersonationApps,
  resolveImpersonateErrorMessageKey,
} from "./impersonate.utils";

const DEFAULT_TTL_MINUTES = 30;

export type ImpersonateUserModalProps = {
  user: { id: string; name: string; surname: string };
  onOpenChange: (open: boolean) => void;
};

export const ImpersonateUserModal = ({ user, onOpenChange }: ImpersonateUserModalProps) => {
  const t = useT();
  const {
    data: allowedAppsData,
    isPending: isAllowedAppsPending,
    isError: isAllowedAppsError,
  } = useDashboardImpersonationControllerAllowedApps(user.id, {
    query: { staleTime: 30_000 },
  });
  const availableApps = resolveAvailableImpersonationApps(allowedAppsData?.allowedApps);
  const [app, setApp] = useState<LoginApp>(LoginApp.dashboard);
  const [ttlMinutes, setTtlMinutes] = useState<number | null>(DEFAULT_TTL_MINUTES);
  const { mutateAsync: impersonateUser, isPending } =
    useDashboardImpersonationControllerImpersonate();

  const userLabel = getUserFullName(user) || t("general.text.unknownUser");

  useEffect(() => {
    if (availableApps.length > 0 && !availableApps.includes(app)) {
      setApp(availableApps[0]);
    }
  }, [app, availableApps]);

  const handleConfirm = async () => {
    if (!availableApps.includes(app)) return;

    const ttl = ttlMinutes && ttlMinutes > 0 ? ttlMinutes : DEFAULT_TTL_MINUTES;

    try {
      const res = await impersonateUser({
        data: { targetUserPublicId: user.id, ttlMinutes: ttl, app },
      });

      if (!res?.accessToken) {
        toast.error(t("user.impersonation.errorState.failed"));
        return;
      }

      // The backend resolves the target user's default landing scope (their
      // default platform + that platform's owner federation). Open the new tab
      // already scoped to that pair so the impersonated session lands on a
      // consistent platform/entity with no client-side resolution. Fall back to
      // root only when the user has no available platform.
      const landingPath =
        app === LoginApp.dashboard
          ? res.defaultAvailablePlatformId && res.defaultAvailableFederationId
            ? buildScopedPath(
                res.defaultAvailablePlatformId,
                res.defaultAvailableFederationId,
                null
              )
            : "/"
          : app === LoginApp.manager
            ? "/dashboard/all-tournaments"
            : "/";

      const origin =
        app === LoginApp.dashboard
          ? window.location.origin
          : app === LoginApp.manager
            ? getTournamentUrl()
            : env.clientUrl;

      const url = buildImpersonationUrl(origin, landingPath, {
        accessToken: res.accessToken,
        expiresAt: res.expiresAt,
        // Public ids (usr_…) — stored verbatim; informational only.
        targetUserId: res.targetUserId,
        impersonatorId: res.impersonatorId,
      });

      // New tab acts as the target user; the admin's own tab stays untouched.
      window.open(url, "_blank", "noopener,noreferrer");
      onOpenChange(false);
    } catch (error) {
      // Translate the backend's error code (never the raw message): the 403 for a
      // target without dashboard access maps to a specific copy, everything else
      // falls back to the generic failure message.
      const code = getApiErrorCode(error);
      toast.error(t(resolveImpersonateErrorMessageKey(code)));
    }
  };

  return (
    <ActionModal
      title={t("user.impersonation.modal.title")}
      primaryActionLabel={t("user.impersonation.modal.confirm")}
      size="xl"
      variant="danger"
      isLoading={isPending}
      isDisabled={isAllowedAppsPending || isAllowedAppsError || availableApps.length === 0}
      onAction={handleConfirm}
      onOpenChange={onOpenChange}
    >
      <div className="flex flex-col gap-4">
        <Alert
          variant="warning"
          hideCloseButton
          description={t("user.impersonation.modal.warning", { user: userLabel })}
        />
        {isAllowedAppsPending ? (
          <Skeleton className="h-[88px]" />
        ) : isAllowedAppsError ? (
          <Alert
            variant="danger"
            hideCloseButton
            description={t("user.impersonation.errorState.availabilityFailed")}
          />
        ) : (
          <RadioBoxGroup
            label={t("user.impersonation.modal.appLabel")}
            value={app}
            classNames={{ itemsWrapper: "flex flex-col gap-2.5 sm:flex-row" }}
            onChange={(value) => setApp(value as LoginApp)}
          >
            {availableApps.map((availableApp) => (
              <RadioBox
                key={availableApp}
                value={availableApp}
                label={t(`user.impersonation.modal.apps.${availableApp}.label`)}
                description={t(`user.impersonation.modal.apps.${availableApp}.description`)}
                className="flex-1 min-w-0"
              />
            ))}
          </RadioBoxGroup>
        )}
        <NumberField
          label={t("user.impersonation.modal.ttlLabel")}
          description={t("user.impersonation.modal.ttlHint")}
          minValue={1}
          value={ttlMinutes}
          onChange={setTtlMinutes}
        />
      </div>
    </ActionModal>
  );
};
